/**
 * TWCG-1861 — Collaborator cursor overlay for the invite step illustration.
 * Renders a cursor arrow + colored pill label, positioned absolutely within the board.
 */
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

type CursorProps = {
	color: string;
	accentColor?: string;
	label?: string;
	top: number;
	left: number;
	arrowDirection?: 'bottom-left' | 'top-left';
};

const cursorContainerCss = css({
	position: 'absolute',
	pointerEvents: 'none',
	zIndex: 10,
});

const pillWrapperCss = css({
	position: 'relative',
});

const arrowBottomLeftCss = css({
	position: 'absolute',
	insetBlockEnd: '-16px',
	insetInlineStart: '-11px',
});

const arrowTopLeftCss = css({
	position: 'absolute',
	insetBlockStart: '-15px',
	insetInlineStart: '-12px',
});

const pillCss = css({
	borderRadius: '100px',
	paddingBlock: '5px',
	paddingInline: '8px',
	font: token('font.body.small'),
	fontWeight: token('font.weight.semibold'),
	color: token('color.text.inverse'),
	whiteSpace: 'nowrap',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	minHeight: '24px',
});

const pillTextlessCss = css({
	minWidth: token('space.800'),
});

const pillInnerBarCss = css({
	width: '100%',
	height: '3px',
	borderRadius: '100px',
});

const arrowSvg = (color: string) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="15"
		height="15"
		viewBox="0 0 15 15"
		fill="none"
		aria-hidden
	>
		<path
			d="M4.9974 0.000650694L0.00043615 14.6039L14.5808 9.58398L8.19205 6.38955L4.9974 0.000650694Z"
			fill={color}
		/>
	</svg>
);

export const JfindIllustrationCollaboratorCursor = ({
	color,
	accentColor,
	label,
	top,
	left,
	arrowDirection = 'bottom-left',
}: CursorProps) => (
	<div
		css={cursorContainerCss}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
		style={{ top, left }}
	>
		{/* Pill with absolutely positioned arrow */}
		<div css={pillWrapperCss}>
			{arrowDirection === 'top-left' ? (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				<div css={arrowTopLeftCss} style={{ transform: 'rotate(90deg)' }}>
					{arrowSvg(color)}
				</div>
			) : (
				<div css={arrowBottomLeftCss}>{arrowSvg(color)}</div>
			)}
			<div
				css={[pillCss, !label && pillTextlessCss]}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, local/ensure-wac-token-usage
				style={{ backgroundColor: color }}
			>
				{label ? (
					label
				) : (
					<div
						css={pillInnerBarCss}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, local/ensure-wac-token-usage
						style={{ backgroundColor: accentColor ?? token('color.text.inverse') }}
					/>
				)}
			</div>
		</div>
	</div>
);
